import React from "react";
import { Button as BadsButton } from "@hm-group/bads-react";
import BadsIcon from "../Icons/Icons";

interface ButtonProps {
  disabled?: boolean;
  type?: "solid" | "outlined" | "text";
  mode?:
    "primary" | "secondary" | "error" | "warning" | "success" | "information";
  iconColor?: string;
  leftIconType?: string;
  rightIconType?: string;
  size?: "normal" | "small" | "large" | "xsmall";
  text?: string;
  name?: string;
  /**
   * Defaults to "table-settings-button" because that is what this rendered on
   * EVERY button, hardcoded — ten call sites shared one id. Several tests query
   * by it, so it stays the default rather than becoming a silent breakage; pass
   * your own to opt out, which is what any new button should do.
   */
  testId?: string;
  onClick: (name: string) => void;
}

/**
 * The design system's button, from the npm package.
 *
 * This rendered `<bads-button-v3>` — a custom element that existed only because
 * useBads injected the deprecated CDN loader, so the app pulled BADS in twice.
 * The props are unchanged so the ten call sites did not have to move.
 *
 * The click plumbing goes with it: the web component could not take a function,
 * so this generated a per-instance event key, dispatched through `window`, and
 * added a listener for it. The npm component takes onClick directly.
 */
const Button = ({
  disabled = false,
  type = "solid",
  mode = "primary",
  iconColor,
  leftIconType,
  rightIconType,
  size = "normal",
  text = "",
  name = "",
  testId = "table-settings-button",
  onClick,
}: ButtonProps) => (
  <BadsButton
    testId={testId}
    disabled={disabled}
    type={type}
    mode={mode}
    size={size}
    name={name}
    onClick={({ name: clickedName }) => onClick(clickedName)}
    leftIcon={
      leftIconType ? (
        <BadsIcon type={leftIconType} size="small" color={iconColor} />
      ) : undefined
    }
    rightIcon={
      rightIconType ? (
        <BadsIcon type={rightIconType} size="small" color={iconColor} />
      ) : undefined
    }
  >
    {text}
  </BadsButton>
);

export default Button;
